'use strict';

/**
 * Account Profile Status Loader
 * Handles AJAX loading of profile status for full page caching support
 * 
 * @author webstore-team
 */

const localStorageSlim = require('localstorage-slim');
const { removeItem } = require('public/js/lib/local-storage');
const { clearCachedCsrfTokens } = require('public/js/lib/csrf/ajax-hooks');
const { isContextualLoadingDisabled } = require('public/js/lib/contextual-loading-qa');

const PROFILE_CACHE_KEY = 'rp:profile:v1';
const PROFILE_ENDPOINT = '/ajax/profile/status';

// Links to auth pages and Button for login form.
const AUTH_ENTRY_CLICK_SELECTOR = 'a[href*="account/login"], a[href*="account/signup"], a[href*="/login-sso/"], #login-user-form .login-submit-button';
const AUTH_ENTRY_FORM_SELECTOR = 'form[action*="account/login"], form[action*="account/signup"]';

// Invalidate header profile cache for all login routes to account for SSO
function shouldInvalidateForAuthCallback() {
	if (typeof window === 'undefined' || !window.location) {
		return false;
	}

	const { pathname } = window.location;

	return pathname.indexOf('/account/login') === 0
		|| pathname.indexOf('/account/signup') === 0
		|| pathname.indexOf('/login/') === 0
		|| pathname.indexOf('/login-sso/') === 0;
}

/**
 * Get cached profile status from localStorage
 */
function getCachedProfileStatus() {
	if (isContextualLoadingDisabled()) {
		return null;
	}

	try {
		const cached = localStorageSlim.get(PROFILE_CACHE_KEY, { decrypt: false });
		return cached;
	} catch (e) {
		rp_app.logger.error({
			message: 'Failed to get cached profile status',
			error: e,
		});
		return null;
	}
}

/**
 * Cache profile status in localStorage with TTL
 */
function cacheProfileStatus(profileData) {
	if (isContextualLoadingDisabled()) {
		return;
	}

	try {
		localStorageSlim.set(PROFILE_CACHE_KEY, profileData, { ttl: rp_app.cacheConfig.getTtlFor('profile') });
	} catch (e) {
		rp_app.logger.error({
			message: 'Failed to cache profile status',
			error: e,
		});
	}
}

/**
 * Clear cached profile status
 */
function clearProfileCache() {
	try {
		localStorageSlim.remove(PROFILE_CACHE_KEY);
		// Also clear using standard localStorage for compatibility
		removeItem(PROFILE_CACHE_KEY);
	} catch (e) {
		rp_app.logger.error({
			message: 'Failed to clear profile cache',
			error: e,
		});
	}
}

/**
 * Load profile status via AJAX
 */
function loadProfileStatus() {
	return new Promise((resolve, reject) => {
		// Check cache first
		const cached = getCachedProfileStatus();
		if (cached) {
			resolve(cached);
			return;
		}

		window.$ajax({
			method: 'POST',
			url: PROFILE_ENDPOINT,
			dataType: 'json', 
			success: (response) => {
				if (response && Object.keys(response).length === 0) {
					resolve({});
					return;
				}

				if (response && typeof response.status !== 'undefined') {
					cacheProfileStatus(response);
					resolve(response);
				} else {
					reject(new Error('Invalid response format'));
				}
			},
			error: (error) => {
				rp_app.logger.error({
					message: 'Failed to load profile status',
					error: error,
				});
				reject(new Error('Failed to load profile status'));
			}
		});
	});
}

/**
 * Update account UI based on profile status.
 * Three state containers in the template (visibility via .hidden):
 *   .logged-out     – guest: login/signup + order status
 *   .logged-in      – retail signed-in: profile, orders, logout, wishlist
 *   .wholesale-logged-in – wholesale signed-in: same minus profile link
 */
function updateAccountUI(profileData) {
	const $account = window.rp$('#action_account');
	const $plugin = window.rp$('#plugin_action_account');

	if (!$account.length && !$plugin.length) return;

	const isLoggedIn = profileData?.status === true;
	const username = profileData?.username || null;
	const isWholesale = profileData?.wholesale?.is_wholesale === true;

	if ($account.length) {
		window.rp$('.profile-dropdown.logged-out').toggleClass('hidden', isLoggedIn);
		window.rp$('.profile-dropdown.logged-in').toggleClass('hidden', !isLoggedIn || isWholesale);
		window.rp$('.profile-dropdown.wholesale-logged-in').toggleClass('hidden', !isLoggedIn || !isWholesale);

		const $userNameText = window.rp$('.account-username');
		if ($userNameText.length && username) {
			$userNameText.text(username);
		}
	}

	if ($plugin.length) {
		$plugin.show();
	}
}

/**
 * Initialize profile status loading
 */
function initProfileStatus() {
	if (isContextualLoadingDisabled()) {
		return;
	}

	const accountElement = window.rp$('#action_account');
	const pluginAccountElement = window.rp$('#plugin_action_account');

	// If account element doesn't exist, nothing to update
	if (!accountElement.length && !pluginAccountElement.length) {
		// Account component not present on this page
		return;
	}

	// Server always renders guest view, so we need to load status and update UI
	// Load profile status (will use cache if available for instant-on feel)
	loadProfileStatus()
		.then((profileData) => {
			updateAccountUI(profileData);
		})
		.catch((error) => {
			// On error, keep the default guest view (server-rendered state)
			// This ensures the page is still functional even if AJAX fails
			rp_app.logger.error({
				message: 'Error loading profile status',
				error: error,
			});			
		});
}

/**
 * Clear cache on login/logout
 * This should be called from login/logout handlers
 */
function invalidateProfileCache() {
	clearProfileCache();
	// Reload profile status
	initProfileStatus();
}

// Export for use in other modules
module.exports = {
	initProfileStatus,
	invalidateProfileCache,
	clearProfileCache,
	loadProfileStatus
};

// Auto-initialize on DOM ready. Invalidate profile cache on login/signup/SSO routes and before auth-entry clicks.
if (typeof window !== 'undefined' && window.rp$) {
	window.rp$(document).ready(() => {
		if (shouldInvalidateForAuthCallback()) {
			clearProfileCache();
		}
		initProfileStatus();

		window.rp$(document).on('click', AUTH_ENTRY_CLICK_SELECTOR, () => {
			clearProfileCache();
		});
		window.rp$(document).on('submit', AUTH_ENTRY_FORM_SELECTOR, () => {
			clearProfileCache();
		});
		window.rp$(document).on('click', '.log-out-link', () => {
			clearProfileCache();
			clearCachedCsrfTokens();
		});
		window.rp$(document).on('click', '.account-profile-form-submit', () => {
			clearProfileCache();
		});
	});
}
